import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { createMoneyFormatter, formatJobRateRange } from 'shared/lib/formatters';
import { itemPropTypes } from 'shared/proptypes/AggregationItem';
import { fulltextPropTypes as jobPropTypes } from 'shared/proptypes/Job';
import Score from 'shared/components/Score';
import Tag, { TagList } from 'shared/components/Tag';
import { DEFAULT_CURRENCY } from 'shared/constants';
import { userportal as routes, createRoute, STATIC_BASE_URL } from 'shared/routes';
import CompanyName from 'shared/components/CompanyName';
import { getPathname } from 'shared/lib/jobSearchQuery';
import googleAnalytics from 'userportal/lib/googleAnalytics';
import { selectors as applicationSelectors } from 'userportal/ducks/applications';
import { selectors as offerSelectors } from 'userportal/ducks/offers';
import { selectors as visitedSelectors } from 'userportal/ducks/visited';
import { selectors as favoriteJobsSelectors } from 'userportal/ducks/favoriteJobs';
import { selectors as profileSelectors } from 'userportal/ducks/profile';

import styles from './Preview.module.scss';

const PREVIEW_SKILLS = 3;

const JobPreview = ({
  job,
  loggedIn,
  noLogo,
  // onRoleClick,
  onSkillClick,
  onEmploymentTypeClick,
  onCompanySizeClick,
  onRateClick,
  onCityClick,
  currency,
  withSeoLinks,
  searchItems,
  withSmallTags,
  noScore,
  onClick
}) => {
  const { t, i18n } = useTranslation();
  const [showAllSkills, setShowAllSkills] = useState(false);
  const formatMoney = createMoneyFormatter(i18n.language);
  const visitedDate = useSelector(state => visitedSelectors.getVisitedDate(state, job.id));
  const hasApplication = !!useSelector(state => applicationSelectors.getActiveApplicationByJobId(state, job.id));
  const hasOffer = !!useSelector(state => offerSelectors.getActiveOfferByJobId(state, job.id));
  const isFavorite = useSelector(state => favoriteJobsSelectors.isJobFavorite(state, job.id));
  const profileIgnoredSkills = useSelector(profileSelectors.getIgnoredSkillsSlugsMap);

  const isCitySelected = searchItems.filter(({ type }) => type === 'city').length > 0;
  const isSkillSelected = searchItems.filter(({ type }) => type === 'skill').length > 0;
  const hasIgnoredSkill = (job.skills || []).some(skill => skill.slug in profileIgnoredSkills);
  const employmentTypes = job.employmentTypes || [];

  const hasOneRate = _.uniq(employmentTypes.map(et => et.monthlyRate)).length <= 1;
  const hasRateUpToHidden = (job.employmentTypes || []).some(et => et.hideRateUpTo);
  const rateTooltip = useMemo(() => {
    if (hasRateUpToHidden) {
      return `${t('common:searchItem.money')}: ${t('common:job.rateFrom', {
        amount: formatMoney(job.monthlyRate, currency)
      })}. ${t('common:job.rateFromHint', { companyName: job.company.name })}`;
    }

    if (!hasOneRate) {
      return employmentTypes.map(et => (
        <p key={et.slug}>
          {et.monthlyRateUpTo !== et.monthlyRate
            ? `${t(`common:employment.${et.slug}`)}: ${formatMoney(et.monthlyRate, currency)} - ${formatMoney(et.monthlyRateUpTo, currency)}`
            : `${t(`common:employment.${et.slug}`)}: ${formatMoney(et.monthlyRate, currency)}`
          }
        </p>));
    }

    return job.monthlyRateUpTo !== job.monthlyRate
      ? `${t('common:searchItem.money')}: ${formatMoney(job.monthlyRate, currency)} - ${formatMoney(job.monthlyRateUpTo, currency)}`
      : `${t('common:searchItem.money')}: ${formatMoney(job.monthlyRate, currency)}`;
  }, [hasOneRate, hasRateUpToHidden, job]);

  const handleCityClick = (citySlug, cityTitle) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    onCityClick({ type: 'city', slug: citySlug, title: cityTitle })();
  };

  const rateRange = useMemo(() => formatJobRateRange(t, job.employmentTypes, i18n.language, currency), [job, i18n.language]);

  const getTagLink = (type, slug) => getPathname(searchItems.concat({ type, slug }));

  const jobLocations = useMemo(() => {
    const locations = [];
    Object.values(_.groupBy(job.locations, 'countrySlug')).forEach((countryLocations, i) => {
      if (i > 0) locations.push(', ');
      const cities = countryLocations.filter(({ cityTitle }) => !!cityTitle);
      cities.forEach((city, j) => {
        if (j > 0) locations.push(', ');
        locations.push(withSeoLinks && !isCitySelected && onCityClick
          ? <a key={city.citySlug} href={getTagLink('city', city.citySlug)} onClick={handleCityClick(city.citySlug, city.cityTitle)}>{city.cityTitle}</a>
          : city.cityTitle);
      });

      const countries = countryLocations.filter(({ countryTitle }) => !!countryTitle);
      if (!countries.length) return;
      if (cities.length > 0) {
        locations.push(` (${countries[0].countryTitle})`);
      } else {
        locations.push(countries[0].countryTitle);
      }
    });

    return locations;
  }, [job.locations]);

  const onLinkClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const [previewSkills, restSkills] = useMemo(() => {
    if (!job.skills) {
      return [];
    }

    const hardSkills = job.skills.filter(({ soft }) => !soft);
    const softSkills = job.skills.filter(({ soft }) => soft);
    const allSkills = [...hardSkills, ...softSkills];
    const preview = allSkills.slice(0, PREVIEW_SKILLS);
    const rest = allSkills.slice(PREVIEW_SKILLS, allSkills.length);

    if (rest.length < 2) {
      return [[...preview, ...rest], []];
    }

    return [preview, rest];
  }, [job.skills]);

  return (
    <div className={`row ${styles.job}`}>
      {isFavorite && (
        <i className={cn('las la-heart', styles.favorite)} />
      )}
      <div className={styles.header}>
        {loggedIn && !noScore && (
          <div className={styles.rate}>
            <div>
              <Score
                id={job.id}
                score={loggedIn ? job._score : 0}
                active={loggedIn}
                activateLink={routes.login}
                inactiveJob={job.active === false}
                type="your"
                ignored={hasIgnoredSkill}
              />
            </div>
          </div>
        )}
        <div className={styles.info}>
          {(job.company.logo && !noLogo) && (
            <div className={styles.logo} style={{ backgroundImage: `url(${STATIC_BASE_URL}${job.company.logo})` }} />
          )}
          <h3 className={cn({ [styles.visited]: !!visitedDate })}>
            <Link
              to={createRoute(routes.job.detail, { slug: job.slug })}
              onClick={onLinkClick}
            >
              {job.title}
            </Link>
          </h3>
          <CompanyName
            className={styles.companyName}
            company={job.company}
            linkTo={createRoute(routes.company.detail, { slug: job.company.slug })}
            elementType="span"
            onClick={() => googleAnalytics.event('company_name_click', { source: 'job_preview' })}
          />
          {jobLocations.length > 0 && (
            <span className={styles.location}>
              <i className="la la-map-marker" />{jobLocations}
            </span>
          )}
          {job.active === false && (
            <div className={styles.activeApplication}>
              <i className="la la-eye-slash" />
              {t('userportal:page.job.jobIsNotActive')}
            </div>
          )}
          {hasApplication && (
            <div className={styles.activeApplication}>
              <i className="la la-clipboard-check" />
              {t('userportal:job.activeApplication')}
            </div>
          )}
          {hasOffer && (
            <div className={styles.activeApplication}>
              <i className="la la-clipboard-list" />
              {t('userportal:job.activeOffer')}
            </div>
          )}
          {visitedDate && !hasApplication && (
            <div className={styles.readJob}>
              <i className="la la-eye" /> {t('userportal:job.lastViewed', { date: visitedDate })}
            </div>
          )}
        </div>
      </div>
      {job.onelineSummary && (
        <p className={styles.onelineSummary}>
          {job.onelineSummary}
        </p>
      )}
      <TagList className={styles.tags}>
        {job.monthlyRate && (
          <Tag
            // isSmall={withSmallTags}
            component="li"
            slug={job.currency}
            onClick={onRateClick(job.monthlyRate)}
            type="money"
            tooltip={rateTooltip}
          >
            {rateRange}
          </Tag>
        )}
        {employmentTypes.length > 0 && employmentTypes.map(employment => (
          <Tag
            key={employment.slug}
            // isSmall={withSmallTags}
            component="li"
            slug={employment.slug}
            onClick={onEmploymentTypeClick({ slug: employment.slug })}
            type="employment"
          >
            {t(`common:employment.${employment.slug}`)}
          </Tag>
        ))}
        {employmentTypes.length === 0 && job.employmentTypeIds && job.employmentTypeIds.map(et => (
          <Tag
            key={et}
            // isSmall={withSmallTags}
            component="li"
            slug={et}
            type="employment"
          >
            {et}
          </Tag>
        ))}
        {/* // do not show role in preview
        {job.roles.map(role => (
          <Tag
            key={role.slug}
            isSmall={withSmallTags}
            component="li"
            slug={role.slug}
            onClick={onRoleClick(role)}
            type="role"
            linkUrl={getTagLink('role', role.slug)}
          >
            {role.title}
          </Tag>
        ))}
        */}
        {previewSkills.map(skill => (
          <Tag
            key={skill.slug}
            isSmall // ={withSmallTags}
            component="li"
            slug={skill.slug}
            onClick={onSkillClick(skill)}
            type="skill"
            subType={skill.soft ? 'soft' : null}
            level={skill.levelId}
            isRequired={skill.required}
            ignored={skill.slug in profileIgnoredSkills}
            linkUrl={withSeoLinks && !isSkillSelected ? getTagLink('skill', skill.slug) : null}
          >
            {skill.title}
          </Tag>
        ))}
        {restSkills.length > 0 && !showAllSkills && (
          <Tag
            noIcon
            tooltip={t('userportal:job.showRestSkills', { postProcess: 'interval', count: restSkills.length })}
            isSmall // ={withSmallTags}
            component="li"
            onClick={() => setShowAllSkills(true)}
          >
            {t('userportal:job.restSkills')} ({restSkills.length})
            <i className="la la-arrow-down" />
          </Tag>
        )}
        {restSkills.map(skill => (
          <Tag
            key={skill.slug}
            isSmall // ={withSmallTags}
            component="li"
            slug={skill.slug}
            onClick={onSkillClick(skill)}
            type="skill"
            subType={skill.soft ? 'soft' : null}
            level={skill.levelId}
            isRequired={skill.required}
            ignored={skill.slug in profileIgnoredSkills}
            linkUrl={withSeoLinks && !isSkillSelected ? getTagLink('skill', skill.slug) : null}
            wrapperClassName={!showAllSkills ? styles.hidden : undefined}
          >
            {skill.title}
          </Tag>
        ))}

        {!job.company.agency && job.company.size && (
          <Tag
            isSmall={withSmallTags}
            component="li"
            slug={job.company.size}
            onClick={onCompanySizeClick({ slug: job.company.size })}
            type="companySize"
            useChildrenForTooltip
          >
            {t(`common:companySizeDetail.${job.company.size}`)}
          </Tag>
        )}
      </TagList>
    </div>
  );
};

JobPreview.propTypes = {
  job: PropTypes.shape(jobPropTypes).isRequired,
  // onRoleClick: PropTypes.func,
  onSkillClick: PropTypes.func,
  onEmploymentTypeClick: PropTypes.func,
  onCompanySizeClick: PropTypes.func,
  onRateClick: PropTypes.func,
  onCityClick: PropTypes.func,
  loggedIn: PropTypes.bool,
  noLogo: PropTypes.bool,
  currency: PropTypes.string,
  withSeoLinks: PropTypes.bool,
  searchItems: PropTypes.arrayOf(PropTypes.shape(itemPropTypes)),
  withSmallTags: PropTypes.bool,
  noScore: PropTypes.bool,
  onClick: PropTypes.func,
};

JobPreview.defaultProps = {
  // onRoleClick() {},
  onSkillClick() {},
  onEmploymentTypeClick() {},
  onCompanySizeClick() {},
  onRateClick() {},
  onCityClick: null,
  loggedIn: false,
  noLogo: false,
  currency: DEFAULT_CURRENCY,
  withSeoLinks: false,
  searchItems: [],
  withSmallTags: false,
  noScore: false,
  onClick() {},
};

export default JobPreview;
